import React from "react";

import type { TypographyProps } from "@material-ui/core";
import { makeStyles, createStyles } from "@material-ui/core";
import type { Moment } from "moment-timezone";
import { useTranslation } from "react-i18next";
import { humanizeDateTime, formatDateTime, humanizeSeconds } from "../Utils";

const noWrap = {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap" as const,
};

const useStyles = makeStyles((theme) =>
    createStyles({
        dateTimePrimaryRow: {
            marginBottom: theme.spacing(1),
            ...noWrap,
        },
        dateTimeSecondaryRow: {
            ...theme.typography.body2,
            ...noWrap,
        },
        largeNumber: {
            ...theme.typography.h2,
            color: theme.palette.text.secondary,
        },
        primary: {
            color: theme.palette.primary.main,
        },
        primaryDark: {
            color: theme.palette.primary.dark,
        },
        primaryLight: {
            color: theme.palette.primary.light,
        },
        disabled: {
            color: theme.palette.text.disabled,
        },
    }),
);

type Color = "primary" | "primaryDark" | "primaryLight";

type OwnProps = {
    color?: Color;
    disabled?: boolean;
};

type Props = OwnProps & Pick<TypographyProps, "children">;

export const LargeNumber = (props: Props) => {
    const { color, disabled, ...otherProps } = props;
    const classes = useStyles(props);
    let className = classes.largeNumber;

    if (disabled) {
        className += ` ${classes.disabled}`;
    } else if (color) {
        className += ` ${classes[color]}`;
    }

    return <span className={className} {...otherProps} />;
};

type StyledDateTimeProps = {
    dateTime: Moment;
    timeZone: string;
};

export const StyledDateTime = (props: StyledDateTimeProps) => {
    const { t } = useTranslation();
    const classes = useStyles();
    return (
        <>
            <div className={classes.dateTimePrimaryRow}>
                {props.dateTime && `${humanizeDateTime(props.dateTime)} ${t("Gui.Common.Labels.Ago")}`}
            </div>
            <div className={classes.dateTimeSecondaryRow}>{formatDateTime(props.dateTime, props.timeZone)}</div>
        </>
    );
};

export const StyledDuration = (props: { durationSeconds: number }) => {
    const { t } = useTranslation();
    return (
        <>
            {typeof props.durationSeconds === "number" &&
                `${t("Gui.Common.Duration")} ${humanizeSeconds(props.durationSeconds)}`}
        </>
    );
};
